/**
 * ColorSwatchPicker — buyer-side colour selector used in the rental
 * drawer + the product detail page + anywhere a buyer chooses a colour
 * before adding to cart.
 *
 * Usage:
 *   <ColorSwatchPicker
 *     colors={product.colors}    // [{name, hex}]
 *     value={picked.color}       // selected name
 *     onChange={(name) => setPicked({...picked, color: name})}
 *     testidPrefix={`rental-item-${pid}-color`}
 *   />
 *
 * Renders small swatch circles with the colour name underneath. The
 * active swatch gets a chartreuse ring + checkmark glow so the buyer
 * can see their pick at a glance.
 */
import React from "react";
import { Check } from "lucide-react";


export default function ColorSwatchPicker({ colors = [], value, onChange,
                                            testidPrefix = "color-picker",
                                            size = "md" }) {
  if (!colors.length) return null;
  const dim = size === "sm" ? "w-6 h-6" : size === "lg" ? "w-9 h-9" : "w-7 h-7";

  return (
    <div className="flex flex-wrap gap-2" data-testid={`${testidPrefix}-root`}>
      {colors.map((c) => {
        const active = (value || "").toLowerCase() === c.name.toLowerCase();
        return (
          <button type="button"
            key={c.name}
            onClick={(e) => { e.stopPropagation(); onChange?.(c.name); }}
            data-testid={`${testidPrefix}-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
            title={c.name}
            className={`group inline-flex flex-col items-center gap-1 ${active ? "" : "opacity-90 hover:opacity-100"}`}>
            <span className={`${dim} relative rounded-full border-2 transition flex items-center justify-center shrink-0 ${
                active
                  ? "border-[#D4FF00] shadow-[0_0_0_3px_rgba(212,255,0,0.18)]"
                  : "border-[#3D2845] hover:border-[#A1A1AA]"
              }`}
              style={{ background: c.hex }}>
              {active && (
                <Check size={size === "lg" ? 14 : 11}
                  className="text-white drop-shadow-[0_0_2px_rgba(0,0,0,0.9)]"/>
              )}
            </span>
            <span className={`text-[9px] uppercase tracking-wider font-bold ${
              active ? "text-[#D4FF00]" : "text-[#A1A1AA] group-hover:text-[#FAFAFA]"
            }`}>
              {c.name}
            </span>
          </button>
        );
      })}
    </div>
  );
}
